import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import type { AcousticsResult } from '../acoustics/compute'
import { type Frame, makeFrame, pushOutXZ } from '../acoustics/geometry'
import { ccw, type Pt, segmentSpans } from '../acoustics/polygon'
import { clampToRoom, coreRadius, coreShape, ellipsePerimeter } from '../acoustics/room'
import { MATERIALS, type MaterialId } from '../model/materials'
import { type Face, type Primitive, ROOM_FACES, type SceneDoc, type Source, type Vec3 } from '../model/schema'
import {
  buildTubeGeometry,
  discGeometry,
  ellipseSegments,
  gridSpans,
  outlineSegments,
  polygonGeometry,
  prismGeometry,
  wallsGeometry,
} from './shapes'
import { makeConcreteTexture } from './textures'
import type { AppMode, Selection, ViewMode, ViewportEvents, ViewState } from './types'

const ACCENT = 0xe0a64a
const BG = 0x131517
const FAMILY_COLORS: Record<string, number> = {
  vent: 0x6fb7d8,
  motor: 0xe0a64a,
  drone: 0xc4605a,
  drip: 0x62d2c0,
  turbine: 0xe8c36a,
  fan: 0x8fd1a8,
  press: 0xd98b5f,
  grind: 0xd6d06a,
  creak: 0xa6845c,
  clip: 0xb08be0,
}
/** The texture is mid-grey; boost the tint so surfaces read as their material colour under restrained light. */
const MATERIAL_BOOST: Record<MaterialId, number> = { concrete: 1.9, metal: 1.7, absorbent: 2.2 }
const EYE_RADIUS = 0.3
const WALK_SPEED = 3.2 // m/s in a normal room; scaled up for megastructures (see walkScale)
const RUN_FACTOR = 2.4
const BASE_TILE = 3 // metres per concrete texture tile in a normal room
const MARKER_PIXELS = 20

/** Smallest 1-2-5 × 10ⁿ step that is at least `target`. */
function niceStep(target: number): number {
  const pow = 10 ** Math.floor(Math.log10(Math.max(target, 1e-6)))
  for (const m of [1, 2, 5, 10]) if (m * pow >= target) return Math.max(1, m * pow)
  return 10 * pow
}

const tmpV = new THREE.Vector3()
const tmpE = new THREE.Euler()

interface ObjectEntry {
  mesh: THREE.Mesh
  edges: THREE.LineSegments
  key: string
  /** Tubes own their geometry (rebuilt when size, wall or caps change); other kinds share unit geometry. */
  tube: boolean
  /** Geometry built per object (tubes and prisms) rather than shared. */
  owned: boolean
  geoKey: string
}

interface SourceEntry {
  group: THREE.Group
  body: THREE.Mesh
  stem: THREE.Line
  key: string
}

export class Viewport {
  readonly renderer: THREE.WebGLRenderer
  private scene = new THREE.Scene()
  private persp = new THREE.PerspectiveCamera(55, 1, 0.05, 30000)
  private ortho = new THREE.OrthographicCamera(-10, 10, 10, -10, -20000, 20000)
  private camera: THREE.PerspectiveCamera | THREE.OrthographicCamera = this.persp
  private orbitPersp: OrbitControls
  private orbitOrtho: OrbitControls
  private transform: TransformControls
  private pointer: PointerLockControls
  private raf = 0
  private ro: ResizeObserver
  private disposed = false

  private roomGroup = new THREE.Group()
  private faceMeshes = new Map<Face, THREE.Mesh>()
  private faceTextures = new Map<Face, THREE.CanvasTexture>()
  private roomEdges: THREE.LineSegments | null = null
  private grid: THREE.LineSegments | null = null
  private baseTexture = makeConcreteTexture()
  private materialCache = new Map<string, THREE.MeshStandardMaterial>()
  private objectEntries = new Map<string, ObjectEntry>()
  private sourceEntries = new Map<string, SourceEntry>()
  private head = new THREE.Group()
  private headStem: THREE.Line
  private pathLines = new THREE.Group()
  private lights: THREE.Light[] = []
  private pickables: THREE.Object3D[] = []
  private boxGeo = new THREE.BoxGeometry(1, 1, 1)
  private cylGeo = new THREE.CylinderGeometry(0.5, 0.5, 1, 40, 1)
  private boxEdges = new THREE.EdgesGeometry(this.boxGeo)
  private cylEdges = new THREE.EdgesGeometry(this.cylGeo, 20)
  private sharedGeos: THREE.BufferGeometry[] = []

  private doc: SceneDoc | null = null
  private state: ViewState = {
    selection: null,
    transformMode: 'translate',
    snapEnabled: true,
    snapStep: 0.5,
    view: 'perspective',
    cutaway: true,
    showGrid: true,
  }
  private mode: AppMode = 'edit'
  private lastRoomKey = ''
  private frames: Frame[] = []
  private perspPose: { pos: THREE.Vector3; target: THREE.Vector3 } | null = null
  private savedOrbit: { pos: THREE.Vector3; target: THREE.Vector3 } | null = null
  private keys = new Set<string>()
  private lastTime = performance.now()
  private dragStart: { x: number; y: number } | null = null
  private draggingGizmo = false
  /** Size of the dragged tube when the drag began: tube meshes are not scaled, so the gizmo's scale is applied to this. */
  private dragBase: Vec3 | null = null
  private framed = false
  private exploreLocked = false
  /** User multiplier for walking speed (mouse wheel in Explore). */
  private speedFactor = 1

  constructor(
    private container: HTMLElement,
    private events: ViewportEvents,
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance', logarithmicDepthBuffer: true })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setClearColor(BG)
    this.renderer.domElement.style.display = 'block'
    this.renderer.domElement.tabIndex = 0
    container.appendChild(this.renderer.domElement)

    this.scene.background = new THREE.Color(BG)
    this.scene.add(this.roomGroup, this.head, this.pathLines)
    this.persp.position.set(60, 30, 90)
    this.head.rotation.order = 'YXZ'

    // Lighting: restrained. A soft hemisphere plus one low key light.
    const hemi = new THREE.HemisphereLight(0xdfe3e8, 0x5a564f, 1.9)
    const key = new THREE.DirectionalLight(0xfff0dc, 2.2)
    key.position.set(-30, 60, 40)
    const fill = new THREE.DirectionalLight(0x9db4cc, 0.8)
    fill.position.set(40, 20, -30)
    this.lights.push(hemi, key, fill)
    this.scene.add(...this.lights)

    this.orbitPersp = new OrbitControls(this.persp, this.renderer.domElement)
    this.orbitPersp.enableDamping = false
    this.orbitPersp.maxDistance = 12000
    this.orbitOrtho = new OrbitControls(this.ortho, this.renderer.domElement)
    this.orbitOrtho.enableRotate = false
    this.orbitOrtho.screenSpacePanning = true
    this.orbitOrtho.enabled = false

    this.transform = new TransformControls(this.persp, this.renderer.domElement)
    this.transform.setSize(0.85)
    this.scene.add(this.transform.getHelper())
    this.transform.addEventListener('dragging-changed', (e) => {
      const dragging = !!(e as unknown as { value: boolean }).value
      this.draggingGizmo = dragging
      this.activeOrbit().enabled = !dragging && this.mode === 'edit'
      const sel = this.state.selection
      if (!sel) return
      this.dragBase =
        dragging && sel.kind === 'object' ? ((this.doc?.objects.find((o) => o.id === sel.id)?.size as Vec3 | undefined) ?? null) : null
      if (dragging) this.events.transformStart(sel)
      else this.events.transformEnd(sel)
    })
    this.transform.addEventListener('objectChange', () => this.emitTransform())

    this.pointer = new PointerLockControls(this.persp, this.renderer.domElement)
    this.pointer.addEventListener('lock', () => {
      this.exploreLocked = true
    })
    // Escape (or losing focus) releases pointer lock: that is how Explore ends.
    this.pointer.addEventListener('unlock', () => this.endExplore())

    // Head and its marker stem
    this.buildHead()
    const stemGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, -1, 0)])
    this.headStem = new THREE.Line(stemGeo, new THREE.LineBasicMaterial({ color: 0x8a8f96, transparent: true, opacity: 0.55 }))
    this.sharedGeos.push(stemGeo)
    this.scene.add(this.headStem)

    const el = this.renderer.domElement
    el.addEventListener('pointerdown', this.onPointerDown)
    el.addEventListener('pointerup', this.onPointerUp)
    el.addEventListener('wheel', this.onWheel, { passive: true })
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    window.addEventListener('blur', this.onBlur)

    this.ro = new ResizeObserver(() => this.resize())
    this.ro.observe(container)
    this.resize()
    this.raf = requestAnimationFrame(this.loop)
  }

  // ---- Public API ----------------------------------------------------------------------------------

  setState(patch: Partial<ViewState>) {
    const prev = this.state
    this.state = { ...prev, ...patch }
    if (patch.view && patch.view !== prev.view) this.applyView(prev.view)
    if (patch.cutaway !== undefined || patch.showGrid !== undefined) this.applyRoomVisibility()
    this.applySelectionVisuals()
    this.applySnap()
    this.attachGizmo()
  }

  syncScene(doc: SceneDoc) {
    this.doc = doc
    this.syncRoom(doc)
    this.syncObjects(doc.objects)
    this.syncSources(doc.sources)
    if (this.mode !== 'explore') this.syncHead(doc.listener)
    const core = coreShape(doc.enclosure)
    this.frames = [...doc.objects.map((o) => makeFrame(o)), ...(core ? [makeFrame(core)] : [])]
    this.rebuildPickables()
    this.applySelectionVisuals()
    if (!this.draggingGizmo) this.attachGizmo() // never reconfigure the gizmo mid-drag
    if (!this.framed) {
      this.frameScene()
      this.framed = true
    }
  }

  /** Visualise direct and reflected paths. Pass null to hide. */
  setAcousticsDebug(result: AcousticsResult | null) {
    for (const c of [...this.pathLines.children]) {
      this.pathLines.remove(c)
      ;(c as THREE.LineSegments).geometry.dispose()
      ;((c as THREE.LineSegments).material as THREE.Material).dispose()
    }
    if (!result) return
    const groups: { color: number; opacity: number; pts: number[] }[] = [
      { color: 0x6fcf97, opacity: 1, pts: [] },
      { color: 0xe05d5d, opacity: 1, pts: [] },
      { color: 0x6aa9ff, opacity: 0.8, pts: [] },
      { color: 0xd98be8, opacity: 0.9, pts: [] },
    ]
    for (const s of result.sources) {
      const d = s.direct
      const g = d.obstructionDb > 0.5 ? groups[1]! : groups[0]!
      g.pts.push(...d.points[0]!, ...d.points[1]!)
      for (const e of s.early) {
        if (!e.valid) continue
        const g = e.kind === 'diffraction' ? groups[3]! : groups[2]!
        for (let i = 0; i < e.points.length - 1; i++) g.pts.push(...e.points[i]!, ...e.points[i + 1]!)
      }
    }
    for (const g of groups) {
      if (!g.pts.length) continue
      const geo = new THREE.BufferGeometry()
      geo.setAttribute('position', new THREE.Float32BufferAttribute(g.pts, 3))
      const line = new THREE.LineSegments(
        geo,
        new THREE.LineBasicMaterial({ color: g.color, transparent: g.opacity < 1, opacity: g.opacity, depthTest: false }),
      )
      line.renderOrder = 10
      this.pathLines.add(line)
    }
  }

  get appMode() {
    return this.mode
  }

  /** Enter first-person mode. Must be called from a user gesture. Resolves false if pointer lock was refused. */
  async enterExplore(): Promise<boolean> {
    if (this.mode === 'explore' || !this.doc) return this.mode === 'explore'
    this.savedOrbit = { pos: this.persp.position.clone(), target: this.orbitPersp.target.clone() }
    this.setMode('explore')
    try {
      this.pointer.lock()
    } catch {
      this.setMode('edit')
      return false
    }
    // Pointer lock confirmation is asynchronous; wait for the 'lock' event briefly.
    const ok = await new Promise<boolean>((resolve) => {
      const t = setTimeout(() => resolve(this.exploreLocked), 800)
      const onLock = () => {
        clearTimeout(t)
        this.pointer.removeEventListener('lock', onLock)
        resolve(true)
      }
      this.pointer.addEventListener('lock', onLock)
    })
    if (!ok) this.setMode('edit')
    return ok
  }

  exitExplore() {
    if (this.mode !== 'explore') return
    if (this.pointer.isLocked)
      this.pointer.unlock() // fires 'unlock' → endExplore()
    else this.endExplore()
  }

  private endExplore() {
    if (this.mode !== 'explore') return
    this.exploreLocked = false
    this.setMode('edit')
    this.events.exploreEnded()
  }

  /** Move the editor camera to where the head is ("listen here" in reverse is handled by the host). */
  getCameraPose(): { position: Vec3; yaw: number; pitch: number } {
    // The perspective camera keeps its pose while an orthographic view is active.
    tmpE.setFromQuaternion(this.persp.quaternion, 'YXZ')
    return { position: [this.persp.position.x, this.persp.position.y, this.persp.position.z], yaw: tmpE.y, pitch: tmpE.x }
  }

  /** Client-space pixel where a world point is currently drawn (null if behind the camera). Used by tests. */
  screenPointOf(p: Vec3): { x: number; y: number } | null {
    const v = new THREE.Vector3(...p).project(this.camera)
    if (v.z > 1) return null
    const rect = this.renderer.domElement.getBoundingClientRect()
    return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height }
  }

  /** World point where the pointer ray crosses the horizontal plane at height y (null if it never does). */
  pointOnPlane(clientX: number, clientY: number, y: number): Vec3 | null {
    const rect = this.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1)
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, this.camera)
    const hit = ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -y), new THREE.Vector3())
    return hit ? [hit.x, hit.y, hit.z] : null
  }

  /** Reset the editor camera to frame the whole enclosure. */
  frameScene() {
    if (!this.doc) return
    const [w, h, d] = this.doc.enclosure.size
    const m = Math.max(w, h, d)
    const target = new THREE.Vector3(0, h * 0.3, 0)
    this.persp.position.set(m * 0.5, h * 0.6 + m * 0.2, d * 0.5 + m * 0.85)
    this.orbitPersp.target.copy(target)
    this.orbitPersp.update()
    this.applyView(this.state.view)
  }

  dispose() {
    this.disposed = true
    cancelAnimationFrame(this.raf)
    const el = this.renderer.domElement
    el.removeEventListener('pointerdown', this.onPointerDown)
    el.removeEventListener('pointerup', this.onPointerUp)
    el.removeEventListener('wheel', this.onWheel)
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('blur', this.onBlur)
    this.ro.disconnect()
    if (this.pointer.isLocked) this.pointer.unlock()
    this.pointer.dispose()
    this.transform.dispose()
    this.orbitPersp.dispose()
    this.orbitOrtho.dispose()
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.geometry) m.geometry.dispose()
      const mat = m.material as THREE.Material | THREE.Material[] | undefined
      if (Array.isArray(mat)) for (const x of mat) x.dispose()
      else mat?.dispose()
    })
    for (const g of [this.boxGeo, this.cylGeo, this.boxEdges, this.cylEdges, ...this.sharedGeos]) g.dispose()
    for (const m of this.materialCache.values()) m.dispose()
    for (const t of this.faceTextures.values()) t.dispose()
    this.baseTexture.dispose()
    this.renderer.dispose()
    this.renderer.forceContextLoss()
    el.remove()
  }

  // ---- Mode / camera -------------------------------------------------------------------------------

  private activeOrbit() {
    return this.camera === this.persp ? this.orbitPersp : this.orbitOrtho
  }

  private setMode(mode: AppMode) {
    this.mode = mode
    const explore = mode === 'explore'
    this.head.visible = !explore
    this.headStem.visible = !explore
    this.activeOrbit().enabled = !explore
    this.transform.enabled = !explore
    this.transform.getHelper().visible = !explore
    if (explore) {
      // Camera becomes the head.
      this.camera = this.persp
      if (this.doc) this.poseCameraFromHead(this.doc.listener)
    } else {
      this.keys.clear()
      if (this.savedOrbit) {
        this.persp.position.copy(this.savedOrbit.pos)
        this.orbitPersp.target.copy(this.savedOrbit.target)
        this.persp.up.set(0, 1, 0)
        this.persp.lookAt(this.savedOrbit.target)
        this.orbitPersp.update()
        this.savedOrbit = null
      }
      this.camera = this.state.view === 'perspective' ? this.persp : this.ortho
      this.activeOrbit().enabled = true
      if (this.doc) this.syncHead(this.doc.listener)
    }
    this.attachGizmo()
  }

  private poseCameraFromHead(l: { position: Vec3; yaw: number; pitch: number }) {
    this.persp.up.set(0, 1, 0)
    this.persp.position.set(...l.position)
    this.persp.rotation.order = 'YXZ'
    this.persp.rotation.set(l.pitch, l.yaw, 0)
  }

  private applyView(prev: ViewMode) {
    const v = this.state.view
    if (this.mode === 'explore') return
    if (prev === 'perspective' && v !== 'perspective') {
      this.perspPose = { pos: this.persp.position.clone(), target: this.orbitPersp.target.clone() }
    }
    if (v === 'perspective') {
      this.camera = this.persp
      this.orbitOrtho.enabled = false
      this.orbitPersp.enabled = true
      if (this.perspPose && prev !== 'perspective') {
        this.persp.position.copy(this.perspPose.pos)
        this.orbitPersp.target.copy(this.perspPose.target)
        this.orbitPersp.update()
      }
    } else {
      this.camera = this.ortho
      this.orbitPersp.enabled = false
      this.orbitOrtho.enabled = true
      if (!this.doc) return
      const [w, h, d] = this.doc.enclosure.size
      const c = new THREE.Vector3(0, h / 2, 0)
      const dist = Math.max(w, h, d) * 2
      if (v === 'top') {
        this.ortho.position.set(0, dist, 0)
        this.ortho.up.set(0, 0, -1)
        c.set(0, 0, 0)
      } else if (v === 'front') {
        this.ortho.position.set(0, h / 2, dist)
        this.ortho.up.set(0, 1, 0)
      } else {
        this.ortho.position.set(dist, h / 2, 0)
        this.ortho.up.set(0, 1, 0)
      }
      this.orbitOrtho.target.copy(c)
      this.ortho.lookAt(c)
      const span = v === 'top' ? Math.max(w, d) : v === 'front' ? Math.max(w, h) : Math.max(d, h)
      this.ortho.zoom = this.orthoHalfHeight() / (span * 0.62)
      this.ortho.updateProjectionMatrix()
      this.orbitOrtho.update()
    }
    this.transform.camera = this.camera
  }

  private orthoHalfHeight() {
    return (this.ortho.top - this.ortho.bottom) / 2
  }

  private resize() {
    const w = Math.max(1, this.container.clientWidth)
    const h = Math.max(1, this.container.clientHeight)
    this.renderer.setSize(w, h, false)
    this.renderer.domElement.style.width = '100%'
    this.renderer.domElement.style.height = '100%'
    this.persp.aspect = w / h
    this.persp.updateProjectionMatrix()
    const half = 20
    this.ortho.left = -half * (w / h)
    this.ortho.right = half * (w / h)
    this.ortho.top = half
    this.ortho.bottom = -half
    this.ortho.updateProjectionMatrix()
  }

  // ---- Scene sync ----------------------------------------------------------------------------------

  private material(id: MaterialId, selected = false): THREE.MeshStandardMaterial {
    const key = `${id}:${selected ? 1 : 0}`
    let m = this.materialCache.get(key)
    if (!m) {
      const def = MATERIALS[id]
      m = new THREE.MeshStandardMaterial({
        color: new THREE.Color(def.color).multiplyScalar(MATERIAL_BOOST[id]),
        roughness: id === 'metal' ? 0.42 : 0.92,
        metalness: id === 'metal' ? 0.55 : 0,
        map: id === 'absorbent' ? null : this.baseTexture,
        emissive: selected ? ACCENT : 0x000000,
        emissiveIntensity: selected ? 0.16 : 0,
      })
      this.materialCache.set(key, m)
    }
    return m
  }

  private syncRoom(doc: SceneDoc) {
    const enc = doc.enclosure
    const [w, h, d] = enc.size
    const shape = enc.shape
    const faces = ROOM_FACES[shape]
    const rc = coreRadius(enc)
    const key = `${shape}|${w},${h},${d}|${rc}|${faces.map((f) => enc.materials[f]).join()}|${shape === 'polygon' ? JSON.stringify(enc.outline) : ''}`
    if (key === this.lastRoomKey) return
    this.lastRoomKey = key

    for (const m of this.faceMeshes.values()) {
      this.roomGroup.remove(m)
      m.geometry.dispose()
      ;(m.material as THREE.Material).dispose()
    }
    for (const t of this.faceTextures.values()) t.dispose()
    this.faceMeshes.clear()
    this.faceTextures.clear()

    // Bigger structures get bigger formwork panels, otherwise a kilometre of wall shimmers into noise.
    const tile = Math.min(24, Math.max(BASE_TILE, Math.max(w, h, d) / 40))
    const addFace = (
      face: Face,
      geo: THREE.BufferGeometry,
      facing: 'plane' | 'wall' | 'solid',
      repeat: [number, number],
      place: (m: THREE.Mesh) => void,
    ) => {
      const tex = this.baseTexture.clone()
      tex.needsUpdate = true
      tex.repeat.set(repeat[0], repeat[1])
      const id = enc.materials[face]
      const def = MATERIALS[id]
      const mat = new THREE.MeshStandardMaterial({
        map: id === 'absorbent' ? null : tex,
        color: new THREE.Color(def.color).multiplyScalar(MATERIAL_BOOST[id]),
        metalness: id === 'metal' ? 0.5 : 0,
        roughness: id === 'metal' ? 0.45 : 0.95,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.userData.facing = facing
      place(mesh)
      this.roomGroup.add(mesh)
      this.faceMeshes.set(face, mesh)
      this.faceTextures.set(face, tex)
    }

    const outline: number[] = []
    if (shape === 'box') {
      const flat = (face: Face, pos: Vec3, rot: Vec3, sx: number, sy: number) =>
        addFace(face, new THREE.PlaneGeometry(1, 1), 'plane', [sx / tile, sy / tile], (m) => {
          m.position.set(...pos)
          m.rotation.set(...rot)
          m.scale.set(sx, sy, 1)
        })
      flat('floor', [0, 0, 0], [-Math.PI / 2, 0, 0], w, d)
      flat('ceiling', [0, h, 0], [Math.PI / 2, 0, 0], w, d)
      flat('north', [0, h / 2, -d / 2], [0, 0, 0], w, h)
      flat('south', [0, h / 2, d / 2], [0, Math.PI, 0], w, h)
      flat('west', [-w / 2, h / 2, 0], [0, Math.PI / 2, 0], d, h)
      flat('east', [w / 2, h / 2, 0], [0, -Math.PI / 2, 0], d, h)
    } else if (shape === 'polygon') {
      const c = ccw(enc.outline as Pt[])
      addFace('wall', wallsGeometry(c, h, tile), 'plane', [1, 1], () => {})
      addFace('floor', polygonGeometry(c, false), 'plane', [1 / tile, 1 / tile], (m) => {
        m.rotation.x = -Math.PI / 2
      })
      addFace('ceiling', polygonGeometry(c, true), 'plane', [1 / tile, 1 / tile], (m) => {
        m.rotation.x = Math.PI / 2
        m.position.y = h
      })
      outline.push(...outlineSegments(c, h))
    } else {
      const rx = w / 2
      const rz = d / 2
      const perimeter = ellipsePerimeter(rx, rz)
      addFace('wall', new THREE.CylinderGeometry(1, 1, 1, 128, 1, true), 'wall', [perimeter / tile, h / tile], (m) => {
        m.scale.set(rx, h, rz)
        m.position.set(0, h / 2, 0)
      })
      addFace('floor', discGeometry(rx, rz, rc), 'plane', [1 / tile, 1 / tile], (m) => {
        m.rotation.x = -Math.PI / 2
      })
      addFace('ceiling', discGeometry(rx, rz, rc), 'plane', [1 / tile, 1 / tile], (m) => {
        m.rotation.x = Math.PI / 2
        m.position.y = h
      })
      outline.push(...ellipseSegments(rx, rz, 0), ...ellipseSegments(rx, rz, h))
      if (rc > 0) {
        addFace('core', new THREE.CylinderGeometry(1, 1, 1, 96, 1, false), 'solid', [(2 * Math.PI * rc) / tile, h / tile], (m) => {
          m.scale.set(rc, h, rc)
          m.position.set(0, h / 2, 0)
        })
        outline.push(...ellipseSegments(rc, rc, 0), ...ellipseSegments(rc, rc, h))
      }
    }
    this.applyRoomVisibility()

    // Outline of the shell + floor grid for scale.
    if (this.roomEdges) {
      this.roomGroup.remove(this.roomEdges)
      this.roomEdges.geometry.dispose()
    }
    let edgeGeo: THREE.BufferGeometry
    if (shape === 'box') {
      const boxGeo = new THREE.BoxGeometry(w, h, d)
      edgeGeo = new THREE.EdgesGeometry(boxGeo)
      edgeGeo.translate(0, h / 2, 0)
      boxGeo.dispose()
    } else {
      edgeGeo = new THREE.BufferGeometry()
      edgeGeo.setAttribute('position', new THREE.Float32BufferAttribute(outline, 3))
    }
    this.roomEdges = new THREE.LineSegments(edgeGeo, new THREE.LineBasicMaterial({ color: 0x5a5f66 }))
    this.roomGroup.add(this.roomEdges)
    this.buildGrid(shape, w, d, rc, shape === 'polygon' ? ccw(enc.outline as Pt[]) : null)

    this.scene.fog = new THREE.Fog(BG, Math.max(w, d) * 0.9, Math.max(w, d, h) * 3.6)
    const lightScale = Math.max(w, h, d)
    ;(this.lights[1] as THREE.DirectionalLight).position.set(-0.4 * lightScale, 1.2 * lightScale, 0.5 * lightScale)
    ;(this.lights[2] as THREE.DirectionalLight).position.set(0.6 * lightScale, 0.4 * lightScale, -0.5 * lightScale)
    if (this.framed) this.applyView(this.state.view)
  }

  private buildGrid(shape: string, w: number, d: number, rc: number, contour: Pt[] | null) {
    if (this.grid) {
      this.roomGroup.remove(this.grid)
      this.grid.geometry.dispose()
    }
    const minor: number[] = []
    const major: number[] = []
    const y = 0.02
    const push = (arr: number[], x1: number, z1: number, x2: number, z2: number) => arr.push(x1, y, z1, x2, y, z2)
    // Spacing grows with the room (1, 2, 5, 10 … m) so the lines stay readable; every tenth line is a major one.
    const step = niceStep(Math.max(w, d) / 90)
    const majorStep = step * 10
    const lines = (half: number, emit: (c: number, major: boolean) => void) => {
      for (let c = Math.ceil(-half / step) * step; c <= half; c += step) emit(c, Math.abs(c % majorStep) < 1e-6)
    }
    if (contour) {
      // Custom outline: clip every grid line to the parts that lie inside it.
      lines(w / 2, (x, mj) => {
        for (const [t0, t1] of segmentSpans([contour], x, -d / 2 - 1, x, d / 2 + 1)) {
          push(mj ? major : minor, x, -d / 2 - 1 + t0 * (d + 2), x, -d / 2 - 1 + t1 * (d + 2))
        }
      })
      lines(d / 2, (z, mj) => {
        for (const [t0, t1] of segmentSpans([contour], -w / 2 - 1, z, w / 2 + 1, z)) {
          push(mj ? major : minor, -w / 2 - 1 + t0 * (w + 2), z, -w / 2 - 1 + t1 * (w + 2), z)
        }
      })
    } else if (shape === 'box') {
      lines(w / 2, (x, mj) => push(mj ? major : minor, x, -d / 2, x, d / 2))
      lines(d / 2, (z, mj) => push(mj ? major : minor, -w / 2, z, w / 2, z))
    } else {
      const rx = w / 2
      const rz = d / 2
      lines(rx, (x, mj) => {
        for (const [a, b] of gridSpans(x, 'z', rx, rz, rc)) push(mj ? major : minor, x, a, x, b)
      })
      lines(rz, (z, mj) => {
        for (const [a, b] of gridSpans(z, 'x', rx, rz, rc)) push(mj ? major : minor, a, z, b, z)
      })
    }
    const geo = new THREE.BufferGeometry()
    const pts = [...minor, ...major]
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3))
    const colors: number[] = []
    const nMinor = minor.length / 3
    for (let i = 0; i < pts.length / 3; i++) {
      const c = i < nMinor ? [0.33, 0.34, 0.36] : [0.5, 0.5, 0.52]
      colors.push(...c)
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
    this.grid = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.55 }))
    this.grid.visible = this.state.showGrid
    this.roomGroup.add(this.grid)
  }

  private applyRoomVisibility() {
    for (const m of this.faceMeshes.values()) {
      const facing = m.userData.facing as 'plane' | 'wall' | 'solid'
      // Cutaway: surfaces are one-sided and face inward, so the ones between camera and interior vanish.
      // Planes face inward by construction; a cylinder's front faces point out, so its inward side is the back.
      const side =
        facing === 'solid'
          ? THREE.FrontSide
          : this.state.cutaway
            ? facing === 'wall'
              ? THREE.BackSide
              : THREE.FrontSide
            : THREE.DoubleSide
      const mat = m.material as THREE.MeshStandardMaterial
      if (mat.side !== side) {
        mat.side = side
        mat.needsUpdate = true
      }
    }
    if (this.grid) this.grid.visible = this.state.showGrid
  }
  private objectKey(o: Primitive) {
    return `${o.kind}|${o.material}`
  }

  /** What a tube's own geometry depends on. Resizing a tube rebuilds it in place (the gizmo keeps its mesh). */
  private geoKeyOf(o: Primitive) {
    if (o.kind === 'prism') return JSON.stringify([o.outline, o.holes])
    return o.kind === 'tube' ? `${o.size.join()}|${o.wall}|${o.caps}` : ''
  }

  /** Geometry that belongs to one object: a tube at its real size, or a prism in unit coordinates. */
  private ownedGeometry(o: Primitive): THREE.BufferGeometry | null {
    if (o.kind === 'tube') return buildTubeGeometry(o.size, o.wall, o.caps)
    if (o.kind === 'prism') return prismGeometry(o.outline as Pt[], o.holes as Pt[][])
    return null
  }

  private syncObjects(objects: Primitive[]) {
    const seen = new Set<string>()
    for (const o of objects) {
      seen.add(o.id)
      let e = this.objectEntries.get(o.id)
      if (e && e.key !== this.objectKey(o)) {
        this.removeObject(o.id)
        e = undefined
      }
      if (!e) {
        const tube = o.kind === 'tube'
        const owned = tube || o.kind === 'prism'
        const geo = this.ownedGeometry(o) ?? (o.kind === 'box' ? this.boxGeo : this.cylGeo)
        const edgeGeo = owned ? new THREE.EdgesGeometry(geo, 25) : o.kind === 'box' ? this.boxEdges : this.cylEdges
        const mesh = new THREE.Mesh(geo, this.material(o.material))
        mesh.rotation.order = 'YXZ'
        mesh.userData.sel = { kind: 'object', id: o.id } satisfies Selection
        const edges = new THREE.LineSegments(edgeGeo, new THREE.LineBasicMaterial({ color: 0x2a2c30, transparent: true, opacity: 0.9 }))
        mesh.add(edges)
        this.scene.add(mesh)
        e = { mesh, edges, key: this.objectKey(o), tube, owned, geoKey: this.geoKeyOf(o) }
        this.objectEntries.set(o.id, e)
      } else if (e.owned && e.geoKey !== this.geoKeyOf(o)) {
        e.mesh.geometry.dispose()
        e.edges.geometry.dispose()
        e.mesh.geometry = this.ownedGeometry(o)!
        e.edges.geometry = new THREE.EdgesGeometry(e.mesh.geometry, 25)
        e.geoKey = this.geoKeyOf(o)
      }
      e.mesh.position.set(...o.position)
      e.mesh.rotation.set(o.rotation[0], o.rotation[1], o.rotation[2])
      // Tube geometry is built at its real size so the wall keeps its thickness; other kinds are unit shapes scaled to size.
      if (e.tube) e.mesh.scale.set(1, 1, 1)
      else e.mesh.scale.set(...o.size)
    }
    for (const id of [...this.objectEntries.keys()]) if (!seen.has(id)) this.removeObject(id)
  }

  private removeObject(id: string) {
    const e = this.objectEntries.get(id)
    if (!e) return
    this.scene.remove(e.mesh)
    ;(e.edges.material as THREE.Material).dispose()
    if (e.owned) {
      e.mesh.geometry.dispose()
      e.edges.geometry.dispose()
    }
    this.objectEntries.delete(id)
    if (this.state.selection?.kind === 'object' && this.state.selection.id === id) this.transform.detach()
  }
  private syncSources(sources: Source[]) {
    const seen = new Set<string>()
    for (const s of sources) {
      seen.add(s.id)
      const color = s.kind === 'clip' ? FAMILY_COLORS.clip! : FAMILY_COLORS[s.family]!
      let e = this.sourceEntries.get(s.id)
      const key = `${color}`
      if (e && e.key !== key) {
        this.removeSource(s.id)
        e = undefined
      }
      if (!e) e = this.buildSourceMarker(s.id, color)
      e.group.position.set(...s.position)
      e.body.material = this.markerMaterial(color, s.playing && !s.mute)
      e.group.userData.baseY = s.position[1]
    }
    for (const id of [...this.sourceEntries.keys()]) if (!seen.has(id)) this.removeSource(id)
  }

  private markerMaterials = new Map<string, THREE.MeshBasicMaterial>()
  private markerMaterial(color: number, active: boolean) {
    const key = `${color}:${active}`
    let m = this.markerMaterials.get(key)
    if (!m) {
      m = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: active ? 1 : 0.4, depthTest: true })
      this.markerMaterials.set(key, m)
    }
    return m
  }

  private buildSourceMarker(id: string, color: number): SourceEntry {
    const group = new THREE.Group()
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.22, 20, 14), this.markerMaterial(color, true))
    const ringGeo = new THREE.TorusGeometry(0.4, 0.025, 8, 36)
    const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.7 })
    const ring1 = new THREE.Mesh(ringGeo, ringMat)
    const ring2 = new THREE.Mesh(ringGeo, ringMat)
    ring2.rotation.x = Math.PI / 2
    const hit = new THREE.Mesh(new THREE.SphereGeometry(0.6, 8, 6), new THREE.MeshBasicMaterial({ visible: false }))
    group.add(body, ring1, ring2, hit)
    const stemGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, -1, 0)])
    const stem = new THREE.Line(stemGeo, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.45 }))
    this.scene.add(stem)
    group.userData.sel = { kind: 'source', id } satisfies Selection
    group.userData.stem = stem
    group.userData.ringMat = ringMat
    group.userData.color = color
    group.userData.ringGeo = ringGeo
    this.scene.add(group)
    const entry: SourceEntry = { group, body, stem, key: `${color}` }
    this.sourceEntries.set(id, entry)
    return entry
  }

  private removeSource(id: string) {
    const e = this.sourceEntries.get(id)
    if (!e) return
    this.scene.remove(e.group, e.stem)
    e.group.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.geometry && m.geometry !== e.group.userData.ringGeo) m.geometry.dispose()
    })
    ;(e.group.userData.ringGeo as THREE.BufferGeometry).dispose()
    ;(e.group.userData.ringMat as THREE.Material).dispose()
    e.stem.geometry.dispose()
    ;(e.stem.material as THREE.Material).dispose()
    this.sourceEntries.delete(id)
    if (this.state.selection?.kind === 'source' && this.state.selection.id === id) this.transform.detach()
  }

  private buildHead() {
    const skin = new THREE.MeshStandardMaterial({ color: 0xcfc7ba, roughness: 0.8 })
    const headMesh = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 18), skin)
    headMesh.scale.set(0.085, 0.11, 0.1)
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.02, 0.06, 12), new THREE.MeshStandardMaterial({ color: 0xe0a64a, roughness: 0.6 }))
    nose.rotation.x = -Math.PI / 2 // cone points toward -Z (forward)
    nose.position.set(0, -0.01, -0.12)
    const earGeo = new THREE.CylinderGeometry(0.045, 0.045, 0.018, 20)
    const left = new THREE.Mesh(earGeo, new THREE.MeshStandardMaterial({ color: 0x6aa9ff, roughness: 0.5, emissive: 0x1a3a66 }))
    const right = new THREE.Mesh(earGeo, new THREE.MeshStandardMaterial({ color: 0xff7a66, roughness: 0.5, emissive: 0x662a1a }))
    left.rotation.z = Math.PI / 2
    right.rotation.z = Math.PI / 2
    left.position.set(-0.098, 0, 0)
    right.position.set(0.098, 0, 0)
    const arrowGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, -0.15), new THREE.Vector3(0, 0, -0.55)])
    const arrow = new THREE.Line(arrowGeo, new THREE.LineBasicMaterial({ color: ACCENT }))
    const hit = new THREE.Mesh(new THREE.SphereGeometry(0.5, 8, 6), new THREE.MeshBasicMaterial({ visible: false }))
    this.head.add(headMesh, nose, left, right, arrow, hit)
    this.head.userData.sel = { kind: 'head' } satisfies Selection
    this.head.userData.skin = skin
    this.sharedGeos.push(arrowGeo, earGeo)
  }

  private syncHead(l: { position: Vec3; yaw: number; pitch: number }) {
    this.head.position.set(...l.position)
    this.head.rotation.set(l.pitch, l.yaw, 0)
    this.updateStem(this.headStem, l.position)
  }

  private updateStem(stem: THREE.Line, pos: Vec3) {
    stem.position.set(pos[0], pos[1], pos[2])
    stem.scale.set(1, Math.max(0.01, pos[1]), 1)
  }

  private rebuildPickables() {
    this.pickables = [
      ...[...this.objectEntries.values()].map((e) => e.mesh),
      ...[...this.sourceEntries.values()].map((e) => e.group),
      this.head,
    ]
  }

  // ---- Selection / gizmo ---------------------------------------------------------------------------

  private selectedObject(): THREE.Object3D | null {
    const sel = this.state.selection
    if (!sel) return null
    if (sel.kind === 'object') return this.objectEntries.get(sel.id)?.mesh ?? null
    if (sel.kind === 'source') return this.sourceEntries.get(sel.id)?.group ?? null
    return this.head
  }

  private applySelectionVisuals() {
    const sel = this.state.selection
    for (const [id, e] of this.objectEntries) {
      const selected = sel?.kind === 'object' && sel.id === id
      const o = this.doc?.objects.find((x) => x.id === id)
      if (!o) continue
      e.mesh.material = this.material(o.material, selected)
      const lm = e.edges.material as THREE.LineBasicMaterial
      lm.color.set(selected ? ACCENT : 0x2a2c30)
    }
    for (const [id, e] of this.sourceEntries) {
      const selected = sel?.kind === 'source' && sel.id === id
      const ring = e.group.userData.ringMat as THREE.MeshBasicMaterial
      ring.opacity = selected ? 1 : 0.55
      ring.color.set(selected ? 0xffffff : (e.group.userData.color as number))
    }
    ;(this.head.userData.skin as THREE.MeshStandardMaterial).emissive.set(sel?.kind === 'head' ? ACCENT : 0x000000).multiplyScalar(0.35)
  }

  private attachGizmo() {
    const obj = this.mode === 'explore' ? null : this.selectedObject()
    if (!obj) {
      this.transform.detach()
      return
    }
    const sel = this.state.selection!
    let mode = this.state.transformMode
    if (sel.kind === 'source' && mode !== 'translate') mode = 'translate'
    if (sel.kind === 'head' && mode === 'scale') mode = 'translate'
    if (this.transform.object !== obj) this.transform.attach(obj)
    this.transform.setMode(mode)
    this.transform.setSpace(mode === 'scale' ? 'local' : 'world')
    const head = sel.kind === 'head'
    this.transform.showX = !(head && mode === 'rotate')
    this.transform.showZ = !(head && mode === 'rotate')
    this.transform.showY = true
    this.applySnap()
  }

  private applySnap() {
    const { snapEnabled, snapStep } = this.state
    this.transform.setTranslationSnap(snapEnabled ? snapStep : null)
    this.transform.setRotationSnap(snapEnabled ? THREE.MathUtils.degToRad(15) : null)
    this.transform.setScaleSnap(snapEnabled ? snapStep : null)
  }

  private objectSizeFromScale(id: string, obj: THREE.Object3D): Vec3 {
    const base = this.dragBase
    if (this.objectEntries.get(id)?.tube && base) return [obj.scale.x * base[0], obj.scale.y * base[1], obj.scale.z * base[2]]
    return [obj.scale.x, obj.scale.y, obj.scale.z]
  }

  private emitTransform() {
    const sel = this.state.selection
    const obj = this.transform.object
    if (!sel || !obj) return
    tmpE.setFromQuaternion(obj.quaternion, 'YXZ')
    this.events.transformChange(sel, {
      position: [obj.position.x, obj.position.y, obj.position.z],
      rotation: [tmpE.x, tmpE.y, tmpE.z],
      size: sel.kind === 'object' ? this.objectSizeFromScale(sel.id, obj) : [1, 1, 1],
      yaw: tmpE.y,
      pitch: tmpE.x,
    })
  }

  // ---- Input ---------------------------------------------------------------------------------------

  private onPointerDown = (e: PointerEvent) => {
    this.dragStart = { x: e.clientX, y: e.clientY }
  }

  private onPointerUp = (e: PointerEvent) => {
    const start = this.dragStart
    this.dragStart = null
    if (!start || this.mode !== 'edit' || this.draggingGizmo || e.button !== 0) return
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 4) return
    if (this.transform.axis) return // click landed on the gizmo handles
    const rect = this.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1)
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, this.camera)
    const hits = ray.intersectObjects(this.pickables, true)
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object
      while (o && !o.userData.sel) o = o.parent
      if (o) {
        this.events.pick(o.userData.sel as Selection)
        return
      }
    }
    this.events.pick(null)
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (this.mode !== 'explore') return
    this.keys.add(e.code)
  }
  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code)
  }
  private onBlur = () => this.keys.clear()

  // ---- Frame loop ----------------------------------------------------------------------------------

  private updateMarkerScales() {
    const cam = this.camera
    const viewH = Math.max(1, this.renderer.domElement.clientHeight)
    const scaleFor = (pos: THREE.Vector3, base: number): number => {
      let worldPerPixel: number
      if (cam instanceof THREE.PerspectiveCamera) {
        const dist = cam.position.distanceTo(pos)
        worldPerPixel = (2 * dist * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2))) / viewH
      } else {
        worldPerPixel = (cam.top - cam.bottom) / cam.zoom / viewH
      }
      return Math.max(1, (worldPerPixel * MARKER_PIXELS) / base)
    }
    this.head.scale.setScalar(scaleFor(this.head.position, 0.22))
    for (const e of this.sourceEntries.values()) e.group.scale.setScalar(scaleFor(e.group.position, 0.5))
    for (const e of this.sourceEntries.values()) this.updateStem(e.stem, [e.group.position.x, e.group.position.y, e.group.position.z])
  }

  private walk(dt: number) {
    if (!this.doc) return
    const l = this.doc.listener
    tmpE.setFromQuaternion(this.persp.quaternion, 'YXZ')
    const yaw = tmpE.y
    const pitch = tmpE.x
    let fx = 0
    let fz = 0
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) fz += 1
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) fz -= 1
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) fx += 1
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) fx -= 1
    let x = l.position[0]
    let z = l.position[2]
    if (fx || fz) {
      const len = Math.hypot(fx, fz)
      const speed =
        WALK_SPEED * this.walkScale() * this.speedFactor * (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') ? RUN_FACTOR : 1)
      const dx = (-Math.sin(yaw) * fz + Math.cos(yaw) * fx) / len
      const dz = (-Math.cos(yaw) * fz - Math.sin(yaw) * fx) / len
      // Substep so thin walls cannot be tunnelled at high speed.
      const total = speed * dt
      const steps = Math.max(1, Math.ceil(total / 0.1))
      for (let i = 0; i < steps; i++) {
        x += (dx * total) / steps
        z += (dz * total) / steps
        ;[x, z] = this.resolveCollisions(x, z, l.position[1])
      }
    }
    const moved = x !== l.position[0] || z !== l.position[2]
    const turned = Math.abs(yaw - l.yaw) > 1e-6 || Math.abs(pitch - l.pitch) > 1e-6
    if (moved) {
      this.persp.position.set(x, l.position[1], z)
    }
    if (moved || turned) this.events.walk({ position: [x, l.position[1], z], yaw, pitch })
  }

  /** Walking in a 1 km hall at 3 m/s is a chore: scale the base pace with the room. */
  private walkScale(): number {
    if (!this.doc) return 1
    const m = Math.max(...this.doc.enclosure.size)
    return Math.min(12, Math.max(1, m / 80))
  }

  /** Current walking speed in m/s (without Shift). */
  get walkSpeed(): number {
    return WALK_SPEED * this.walkScale() * this.speedFactor
  }

  private onWheel = (e: WheelEvent) => {
    if (this.mode !== 'explore') return
    this.speedFactor = Math.min(40, Math.max(0.1, this.speedFactor * (e.deltaY < 0 ? 1.25 : 0.8)))
    this.events.speedChanged?.(this.walkSpeed)
  }

  private resolveCollisions(x: number, z: number, eyeY: number): [number, number] {
    if (!this.doc) return [x, z]
    const yMin = Math.max(0, eyeY - 1.6)
    const yMax = eyeY + 0.15
    for (let pass = 0; pass < 3; pass++) {
      for (const f of this.frames) {
        const push = pushOutXZ(f, [x, eyeY, z], EYE_RADIUS, yMin, yMax)
        if (push) {
          x += push[0]
          z += push[2]
        }
      }
      const inside = clampToRoom(this.doc.enclosure, [x, eyeY, z], EYE_RADIUS)
      x = inside[0]
      z = inside[2]
    }
    return [x, z]
  }

  private loop = (now: number) => {
    if (this.disposed) return
    const dt = Math.min(0.1, (now - this.lastTime) / 1000)
    this.lastTime = now
    if (this.mode === 'explore') this.walk(dt)
    else this.activeOrbit().update()
    this.updateMarkerScales()
    this.renderer.render(this.scene, this.camera)
    this.raf = requestAnimationFrame(this.loop)
  }

  /** Debug/test hook: current camera world position. */
  get cameraPosition(): Vec3 {
    this.camera.getWorldPosition(tmpV)
    return [tmpV.x, tmpV.y, tmpV.z]
  }
}
